import {
  DrawerDescription,
  FormMultiSelector,
  DrawerContent,
  DrawerTrigger,
  DrawerHeader,
  ComboboxItem,
  DrawerTitle,
  DrawerClose,
  Typography,
  FormSelect,
  FormInput,
  Loading,
  Drawer,
  Button,
  Label,
  Form,
} from '@ui/components';
import { PropsWithChildren, useEffect, useState, FC } from 'react';
import { FormAssignTable } from '@shared/components/assign-table';
import { formatPhoneNumber } from '@helpers/utils';
import { useLocation } from 'react-router-dom';
import { Cancel } from '@ui/components/icons';

import { useCreateVehicle } from './use-create-vehicle';

export const CreateVehicleDrawer: FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();

  const [open, onOpenChange] = useState<boolean>(false);

  const {
    isCreateVehiclePending,
    vehicleCategoryOptions,
    costCenterOptions,
    assignedEmployees,
    countryOptions,
    assignTable,
    isLoading,
    onSubmit,
    form,
    t,
  } = useCreateVehicle({
    onSuccess: () => {
      form.reset();
      onOpenChange(false);
    },
  });

  useEffect(() => {
    if (location.state?.openVehicleDrawer) {
      onOpenChange(true);
    }
  }, [location.state, onOpenChange]);

  return (
    <Drawer
      onOpenChange={(value) => {
        form.reset();
        onOpenChange(value);
      }}
      open={open}
      handleOnly
    >
      <DrawerTrigger asChild>{children}</DrawerTrigger>
      <DrawerContent className="flex flex-col gap-0 overflow-hidden p-0" draggable="false">
        <DrawerHeader className="flex flex-row items-center justify-between p-4 border-b border-border-weak">
          <DrawerTitle>{t('VEHICLES.CREATE_VEHICLE_DRAWER_HEADER')}</DrawerTitle>
          <DrawerDescription />
          <DrawerClose asChild>
            <Button variant="ghost" size="small" icon>
              <Cancel />
            </Button>
          </DrawerClose>
        </DrawerHeader>
        <form className="flex-1 overflow-hidden flex flex-col gap-2" onSubmit={onSubmit}>
          <Loading isLoading={isLoading}>
            <div className="flex-1 overflow-y-auto p-4 pt-5">
              <div className="space-y-4">
                <Form {...form}>
                  <div className="grid grid-cols-6 gap-2 items-center border-b border-border-weak pb-4">
                    <Label htmlFor="registrationNumber" className="col-span-2">
                      <Typography variant="labelSmallBook" className="text-weak" element="label">
                        {t('VEHICLES.REGISTRATION_NUMBER')}
                      </Typography>
                    </Label>
                    <FormInput
                      componentProps={{
                        id: 'registrationNumber',
                      }}
                      placeholder={t('VEHICLES.REGISTRATION_NUMBER')}
                      className="w-full col-span-4"
                      formControl={form.control}
                      name="registrationNumber"
                      size="small"
                      required
                    />

                    <Label className="col-span-2" htmlFor="name">
                      <Typography variant="labelSmallBook" className="text-weak" element="label">
                        {t('VEHICLES.VEHICLE_NAME')}
                      </Typography>
                    </Label>
                    <FormInput
                      componentProps={{
                        id: 'name',
                      }}
                      placeholder={t('VEHICLES.VEHICLE_NAME')}
                      className="w-full col-span-4"
                      formControl={form.control}
                      size="small"
                      name="name"
                      required
                    />

                    {costCenterOptions.length > 1 && (
                      <>
                        <Label className="col-span-2" htmlFor="costCenters">
                          <Typography
                            variant="labelSmallBook"
                            className="text-weak"
                            element="label"
                          >
                            {t('VEHICLES.COST_CENTER')}
                          </Typography>
                        </Label>
                        <FormMultiSelector
                          placeholder={t('VEHICLES.COST_CENTER')}
                          options={costCenterOptions ?? []}
                          formControl={form.control}
                          className="col-span-4"
                          name="costCenters"
                          size="small"
                        />
                      </>
                    )}

                    <Label htmlFor="registrationCountry" className="col-span-2">
                      <Typography variant="labelSmallBook" className="text-weak" element="label">
                        {t('VEHICLES.REGISTRATION_COUNTRY')}
                      </Typography>
                    </Label>
                    <FormSelect
                      placeholder={t('VEHICLES.REGISTRATION_COUNTRY')}
                      formControl={form.control}
                      name="registrationCountry"
                      options={countryOptions}
                      className="col-span-4"
                    />

                    <Label htmlFor="vehicleCategory" className="col-span-2">
                      <Typography variant="labelSmallBook" className="text-weak" element="label">
                        {t('VEHICLES.VEHICLE_CATEGORY')}
                      </Typography>
                    </Label>
                    <FormSelect
                      placeholder={t('VEHICLES.VEHICLE_CATEGORY')}
                      options={vehicleCategoryOptions}
                      formControl={form.control}
                      className="col-span-4"
                      name="vehicleCategory"
                    />
                  </div>
                  <FormAssignTable
                    itemTemplate={(item) => (
                      <ComboboxItem
                        keywords={[
                          item.firstName ?? '',
                          item.lastName ?? '',
                          item.phoneNumber ?? '',
                        ]}
                        disabled={assignedEmployees?.includes(item.id)}
                        className="justify-between h-12"
                        label={item.id}
                        value={item.id}
                        key={item.id}
                      >
                        <Typography variant="labelMediumBook">
                          {item.firstName ?? '-'} {item.lastName ?? '-'}
                        </Typography>
                        <Typography variant="labelMediumBook">
                          {formatPhoneNumber(item.phoneNumber, item.phoneExt)}
                        </Typography>
                      </ComboboxItem>
                    )}
                    text={{
                      removeButtonText: t('VEHICLES.EMPLOYEES_ASSIGN_REMOVE'),
                      assignButtonText: t('VEHICLES.EMPLOYEES_ASSIGN_ADD'),
                      comboboxPlaceholder: t('VEHICLES.EMPLOYEES_ASSIGN_COMBOBOX_PLACEHOLDER'),
                      comboboxNoResults: t('VEHICLES.EMPLOYEES_ASSIGN_NO_RESULT'),
                      tableNoResults: t('VEHICLES.EMPLOYEES_ASSIGN_NO_RESULT'),
                      searchPlaceholder: t('VEHICLES.TABLE_SEARCH_PLACEHOLDER'),
                      title: t('VEHICLES.EMPLOYEES_ASSIGN_TABLE_TITLE'),
                      selectedRows: (count) =>
                        t('VEHICLES.EMPLOYEES_ASSIGN_SELECTED_ROWS', {
                          postProcess: 'interval',
                          count,
                        }),
                    }}
                    formControl={form.control}
                    name="assignedEmployees"
                    {...assignTable}
                  />
                </Form>
              </div>
            </div>
          </Loading>
          <div className="p-4 shrink-0">
            <Button
              isLoading={isLoading || isCreateVehiclePending}
              disabled={isLoading || isCreateVehiclePending}
              type="submit"
              size="large"
              wide
            >
              {t('VEHICLES.CREATE_VEHICLE_SUBMIT')}
            </Button>
          </div>
        </form>
      </DrawerContent>
    </Drawer>
  );
};
